import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Icon,
  InfoPopover,
  MultiColumnList,
  MCLPagingTypes,
  Tooltip,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { IfPermission } from '@folio/stripes-core';
import Highlighter from 'react-highlight-words';
import { getLocaleLabel } from '../../utils/utils';
import EditTranslationsManager from '../EditTranslations/EditTranslationsManager';

const PAGE_AMOUNT = 50;

/**
 * ResultsPane — displays translation keys in a MultiColumnList.
 *
 * Key improvements over the old version:
 *  - Each row carries the full record including `id` → EditTranslationsManager
 *    can correctly PUT instead of always POST
 *  - Server-side PREV_NEXT pagination via onNeedMoreData
 *  - Selection (checkbox) state lives here for multi-edit/delete actions
 *
 * Props:
 *  contentData     — array of { id, translationKey, value, sourceValue, status,
 *                               localeValue, appName, tableName, categoryName }
 *  totalCount      — total records from API (for pagination)
 *  onNeedMoreData  — callback(askAmount, index) for PREV_NEXT pagination
 *  currentLocale   — selected locale string
 *  category        — categoryName string
 *  searchTerm      — current search term for highlighting
 *  searchActive    — 'true' | 'false'
 *  selectedIndex   — which column is being searched
 *  selection       — { [translationKey]: bool }
 *  setSelection    — setter for selection
 *  paneTitle       — ReactNode
 *  ViewMetaData    — ReactNode
 */
function ResultsPane({
  contentData = [],
  totalCount = 0,
  onNeedMoreData,
  currentLocale,
  category,
  searchTerm = '',
  searchActive = 'false',
  selectedIndex = 'value',
  selection = {},
  setSelection,
  isAssignedTranslator = false,
}) {
  const intl = useIntl();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});

  // ── Selection helpers ─────────────────────────────────────────────
  const onToggleBulkSelection = () => {
    const selectAll = Object.values(selection).includes(false);
    const next = {};
    contentData.forEach((row) => { next[row.translationKey] = selectAll; });
    setSelection(next);
  };

  const onToggleRow = (row) => {
    setSelection({ ...selection, [row.translationKey]: !selection[row.translationKey] });
  };

  // ── Highlighter helper ────────────────────────────────────────────
  const highlight = (text) => (
    <Highlighter
      highlightClassName="YourHighlightClass"
      searchWords={[searchTerm]}
      autoEscape
      textToHighlight={text || ''}
    />
  );

  // ── Column formatters ─────────────────────────────────────────────
  const formatter = {
    selected: (row) => (
      <Checkbox
        name={`selected-${row.translationKey}`}
        checked={!!selection[row.translationKey]}
        onChange={() => onToggleRow(row)}
        onMouseDown={(e) => e.preventDefault()}
      />
    ),
    translationKey: (row) =>
      searchActive === 'true' && selectedIndex === 'translationKey' ? (
        highlight(row.translationKey)
      ) : (
        <InfoPopover content={row.translationKey} icon="key" iconSize="medium" />
      ),
    sourceValue: (row) =>
      searchActive === 'true' && selectedIndex === 'sourceValue'
        ? highlight(row.sourceValue)
        : row.sourceValue,
    value: (row) =>
      searchActive === 'true' && selectedIndex === 'value'
        ? highlight(row.value)
        : row.value,
    status: (row) => (
      <FormattedMessage
        id={`ui-translations.filters.TranslationStatusFilter.${row.status}`}
        defaultMessage={row.status}
      />
    ),
    actions: (row) => (
      <IfPermission perm="ui-translations.edit">
        {isAssignedTranslator && (
          <Tooltip
            id={`translate-tooltip-${row.translationKey}`}
            text={
              <FormattedMessage
                id="stripes-smart-components.buttons.tooltip.translate"
                defaultMessage="Translate to {localeName}"
                values={{ localeName: getLocaleLabel(currentLocale, intl) }}
              />
            }
          >
            {({ ref, ariaIds }) => (
              <Button
                ref={ref}
                aria-labelledby={ariaIds.text}
                buttonStyle={row.value ? 'primary' : 'default'}
                marginBottom0
                onClick={() => { setSelectedItem(row); setOpenEditModal(true); }}
                onMouseDown={(e) => e.preventDefault()}
                disabled={Object.values(selection).includes(true)}
              >
                {row.value ? (
                  <FormattedMessage id="ui-translations.buttons.editItem" />
                ) : (
                  <FormattedMessage id="ui-translations.buttons.translate" />
                )}
              </Button>
            )}
          </Tooltip>
        )}
      </IfPermission>
    ),
  };

  const columnMapping = {
    selected: (
      <Checkbox
        name="selected-all"
        checked={contentData.length > 0 && !Object.values(selection).includes(false)}
        onChange={onToggleBulkSelection}
        onMouseDown={(e) => e.preventDefault()}
      />
    ),
    translationKey: <Icon icon="key" />,
    sourceValue: intl.formatMessage({ id: 'ui-translations.localeTranslations.originTranslation' }),
    value: intl.formatMessage({ id: 'ui-translations.localeTranslations.localeTranslation' }),
    status: intl.formatMessage({ id: 'ui-translations.filters.TranslationStatusFilter.Accordion', defaultMessage: 'Status' }),
    actions: intl.formatMessage({ id: 'stripes-components.paneMenuActionsToggleLabel' }),
  };

  const columnWidths = {
    selected: '4%',
    translationKey: '5%',
    sourceValue: '32%',
    value: '32%',
    status: '12%',
    actions: '15%',
  };

  return (
    <>
      <MultiColumnList
        id="translation-keys-mcl"
        interactive={false}
        visibleColumns={['selected', 'translationKey', 'sourceValue', 'value', 'status', 'actions']}
        columnMapping={columnMapping}
        columnWidths={columnWidths}
        contentData={contentData}
        totalCount={totalCount}
        formatter={formatter}
        onNeedMoreData={onNeedMoreData}
        pageAmount={PAGE_AMOUNT}
        pagingType={MCLPagingTypes.PREV_NEXT}
        autosize
        hasMargin
      />
      <EditTranslationsManager
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        item={selectedItem}
        currentLocale={currentLocale}
        category={category}
      />
    </>
  );
}

ResultsPane.propTypes = {
  contentData: PropTypes.arrayOf(PropTypes.object),
  totalCount: PropTypes.number,
  onNeedMoreData: PropTypes.func,
  currentLocale: PropTypes.string,
  category: PropTypes.string,
  searchTerm: PropTypes.string,
  searchActive: PropTypes.string,
  selectedIndex: PropTypes.string,
  selection: PropTypes.object,
  setSelection: PropTypes.func,
  isAssignedTranslator: PropTypes.bool,
};

export default ResultsPane;
